import numeral from 'numeral'
import CryptoJS from 'crypto-js'
import DayJs from 'dayjs'
import type dayjs from 'dayjs'

const DEFAULT_KEY = 'jctrans2003Z!2@#'
/**
 * 格式化数字，使其更易读。
 * 该函数使用 `numeral` 库将数字转换为指定的格式。
 *
 * @param amount - 需要格式化的数字，可以是字符串或数字类型。
 *                 这种参数类型的灵活性使得它可以处理已经为字符串的输入，也可以处理原始的数字数据。
 * @returns 返回格式化后的字符串，例如 '1,000'。
 */
const formatNum = (amount: string | number) => numeral(amount).format('0,0')
const formatCurrency = (amount: string | number) =>
  numeral(amount).format('0,0+')

function formatViewTimes(count: string | number, isCn: boolean) {
  // 如果count是字符串，则转换为数字
  if (typeof count === 'string') {
    count = Number(count)
  }
  if (count < 1000) {
    return count.toString()
  }
  const formatted = (count / 1000).toFixed(1)
  return formatted.endsWith('.0')
    ? `${parseInt(formatted)}${isCn ? '千' : 'K'}`
    : `${formatted}${isCn ? '千' : 'K'}`
}
/**
 * 解密一个使用默认密钥加密的字符串。
 * 如果输入字符串为空或解密失败，则返回原始字符串。
 *
 * @param word 要解密的加密字符串。
 * @returns 解密后的字符串或解密失败时的原始字符串。
 */
function Decrypt(word: string) {
  // 检查输入字符串是否为空，如果是则直接返回
  if (!word) {
    return word
  }

  // 使用默认密钥创建密钥对象
  const keys = CryptoJS.enc.Utf8.parse(DEFAULT_KEY)

  // 执行AES解密操作
  const decrypt = CryptoJS.AES.decrypt(word, keys, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  })

  try {
    // 将解密结果转换为字符串并检查是否为空
    if (CryptoJS.enc.Utf8.stringify(decrypt).toString() === '') {
      return word
    }
    // 返回解密后的字符串
    return CryptoJS.enc.Utf8.stringify(decrypt).toString()
  } catch (error) {
    // 如果解密过程中发生错误，返回原始字符串
    return word
  }
}

/**
 * 对给定的字符串进行AES加密。
 * 该函数主要用于对字符串进行基本的数据加密。
 *
 * @param word 需要加密的字符串。
 * @returns 返回加密后的字符串，如果输入为空则返回原字符串。
 */
function Encrypt(word: string) {
  // 检查输入字符串是否为空，如果为空则直接返回
  if (!word) {
    return word
  }

  // 使用Utf8编码解析默认密钥
  const keys = CryptoJS.enc.Utf8.parse(DEFAULT_KEY)

  // 使用Utf8编码解析输入字符串
  const srcs = CryptoJS.enc.Utf8.parse(word)

  // 使用AES加密算法进行加密，模式为ECB，填充方式为Pkcs7
  const encrypted = CryptoJS.AES.encrypt(srcs, keys, {
    mode: CryptoJS.mode.ECB,
    padding: CryptoJS.pad.Pkcs7,
  })

  // 将加密结果转换为字符串并返回
  return encrypted.toString()
}

/**
 * 格式化暂停时间
 * 该函数接受一个表示时间的参数，可以是字符串、数字、Date对象、Dayjs对象，或者为null/undefined
 * 它将输入的时间格式化为易于阅读的日期格式
 *
 * @param suspendTime - 暂停时间，可以是多种类型，包括字符串、数字、Date对象、Dayjs对象，或者为null/undefined
 * @returns 返回格式化后的日期字符串，格式为"D-MMM-YYYY"
 */
function formatSuspendTime(
  suspendTime: string | number | Date | DayJs.Dayjs | null | undefined,
) {
  // 使用Dayjs库处理日期，设置语言环境为美国英语，并将日期格式化为"D-MMM-YYYY"格式
  return DayJs(suspendTime).locale('en-US').format('D-MMM-YYYY')
}

/**
 * @description: base64 解密
 * @param {*} originText
 * @return {*}
 */
function base64Decode(encryptedText: string | undefined) {
  if (!encryptedText) {
    return ''
  }
  try {
    let text
    if (import.meta.env.SSR) {
      const decodedBase64 = Buffer.from(encryptedText, 'base64').toString()
      return decodeURIComponent(decodedBase64)
    }
    if (!import.meta.env.SSR && window && window?.atob) {
      text = decodeURIComponent(window?.atob(encryptedText))
    }
    return text || ''
  } catch (err: any) {
    console.error('Decoding error:', err.message)
    return encryptedText
  }
}

function formatAuditTime(time: dayjs.ConfigType) {
  return DayJs(time).locale('en-US').format('D-MMM-YYYY')
}

export default defineNuxtPlugin(() => {
  return {
    provide: {
      hello: (msg: string) => `Hello ${msg}!`,
      formatNum,
      formatCurrency,
      formatViewTimes,
      Encrypt,
      Decrypt,
      formatSuspendTime,
      dayjs: DayJs,
      base64Decode,
      formatAuditTime,
      apiRequest: () => useUseRequest(),
    },
  }
})
